import {
  FirebaseMessaging,
  NotificationReceivedEvent,
} from '@capacitor-firebase/messaging';
import { NotificationActionPerformedEvent } from '@capacitor-firebase/messaging/dist/esm/definitions';
import { Capacitor } from '@capacitor/core';
import { analyticsService } from '../api/analytics';
import { userDB } from '../models/user';
import { EventNames } from '../types/analytics';

interface NotificationData {
  action_type?: string;
  title?: string;
  body?: string;
  announcement_id?: string;
  [key: string]: any;
}

class PushNotificationService {
  permissionsChecked: boolean = false;

  async checkPermissions() {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    const token = await userDB.getTokenFromDB();
    if (!token) return;
    this.permissionsChecked = true;
    const result = await FirebaseMessaging.checkPermissions();
    return result.receive;
  }

  async requestPermissions() {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    const result = await FirebaseMessaging.requestPermissions();
    return result.receive;
  }

  async getToken() {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    const result = await FirebaseMessaging.getToken();
    return result.token;
  }

  /**
   * Adds a listener for when a notification is received while the app is in the foreground.
   */
  async addNotificationReceivedListener(
    callback: (event: NotificationReceivedEvent) => void,
  ) {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    await FirebaseMessaging.addListener('notificationReceived', (event) => {
      const data: NotificationData = event.notification?.data || {};

      // Extract values from the correct sources based on your event structure
      const action: string = data.action_type || '';
      const title: string = event.notification?.title || data.title || '';
      const body: string = event.notification?.body || data.body || '';
      const announcementId: string = data.announcement_id || '';

      analyticsService.trackEvent(EventNames.pushNotificationPopupReceived, {
        epAction: action,
        epNotificationTitle: title,
        epNotificationBody: body,
        epAnnouncementId: announcementId,
      });
      callback(event);
    });
  }

  /**
   * Adds a listener for when a notification action is performed while the app is in the background
   * and the user clicks on the notification.
   */
  async addNotificationActionPerformedListener(
    callback: (event: NotificationActionPerformedEvent) => void,
  ) {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    await FirebaseMessaging.addListener(
      'notificationActionPerformed',
      (event) => {
        const data: NotificationData = event.notification?.data || {};
        // Extract values from data payload (background notifications)
        const action: string = data.action_type || '';
        const title: string = data.title || '';
        const body: string = data.body || '';
        const announcementId: string = data.announcement_id || '';

        analyticsService.trackEvent(EventNames.pushNotificationClicked, {
          epAction: action,
          epNotificationTitle: title,
          epNotificationBody: body,
          epAnnouncementId: announcementId,
        });
        callback(event);
      },
    );
  }
}

export const pushNotificationService = new PushNotificationService();
